import { useEffect, useState } from "react";
import { Plus, Terminal, Trash2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg, fmtDate } from "@/lib/api";
import ConnecteurSonde from "@/components/ConnecteurSonde";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MARQUES = ["loxone", "unv", "unifi", "reyee", "daikin", "heiwa", "autre"];
const INTERVALLES = [5, 10, 15, 30, 60];
const ETATS = {
  en_ligne: { label: "En ligne", classe: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  hors_ligne: { label: "Hors ligne", classe: "text-red-700 bg-red-50 border-red-200" },
  en_attente: { label: "Jamais vu", classe: "text-slate-500 bg-slate-50 border-slate-200" },
};

/** Surveillance active du client : ajout d'un appareil à pinger sans repasser par la recherche. */
export const SondesClient = ({ axonautId, sites = [] }) => {
  const [sondes, setSondes] = useState([]);
  const [form, setForm] = useState({ designation: "", marque: "loxone", intervalle_min: 10, site_id: "" });
  const [connecteur, setConnecteur] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/sondes", { params: { axonaut_id: axonautId } }).then(({ data }) => setSondes(data));

  useEffect(() => {
    setConnecteur(null);
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axonautId]);

  const ajouter = async () => {
    if (form.designation.trim().length < 2) return toast.error("Indiquez le nom de l'appareil");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sondes", { ...form, axonaut_id: axonautId });
      toast.success("Appareil ajouté à la surveillance");
      setForm({ ...form, designation: "" });
      await load();
      setConnecteur(data.id);
    } catch (e) {
      toast.error(errMsg(e, "Ajout impossible"));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (s) => {
    if (!window.confirm(`Retirer « ${s.designation} » de la surveillance ? L'URL de ping ne fonctionnera plus.`)) return;
    await api.delete(`/admin/sondes/${s.id}`);
    toast.success("Surveillance retirée");
    load();
  };

  const url = (s) => `${API}/pings/${s.token}`;
  const enLigne = sondes.filter((s) => s.etat === "en_ligne").length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="client-sondes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="bk-label">Surveillance active (ping)</div>
        <span
          data-testid="client-sondes-badge"
          className={`text-xs px-2 py-1 rounded-full border ${
            sondes.length === 0
              ? "text-slate-500 bg-slate-50 border-slate-200"
              : "text-emerald-700 bg-emerald-50 border-emerald-200"
          }`}
        >
          {sondes.length === 0 ? "Site non surveillé" : `${sondes.length} appareil(s) surveillé(s) · ${enLigne} en ligne`}
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Chaque appareil ajouté reçoit son URL « je vais bien ». S'il se tait pendant deux intervalles, un ticket
        bloquant est créé automatiquement et l'équipe est prévenue.
      </p>

      <div className="grid sm:grid-cols-2 gap-2">
        <Input
          data-testid="client-sonde-designation"
          value={form.designation}
          onChange={(e) => setForm({ ...form, designation: e.target.value })}
          placeholder="Nom de l'appareil (ex : NVR réserve)"
          className="h-10"
        />
        <select
          data-testid="client-sonde-site"
          value={form.site_id}
          onChange={(e) => setForm({ ...form, site_id: e.target.value })}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm"
        >
          <option value="">Site principal</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.nom}</option>
          ))}
        </select>
        <select
          data-testid="client-sonde-marque"
          value={form.marque}
          onChange={(e) => setForm({ ...form, marque: e.target.value })}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm"
        >
          {MARQUES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          data-testid="client-sonde-intervalle"
          value={form.intervalle_min}
          onChange={(e) => setForm({ ...form, intervalle_min: Number(e.target.value) })}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm"
        >
          {INTERVALLES.map((i) => (
            <option key={i} value={i}>toutes les {i} min</option>
          ))}
        </select>
      </div>
      <Button data-testid="client-sonde-add-btn" onClick={ajouter} disabled={busy} variant="outline" size="sm">
        <Plus size={14} className="mr-1" /> {busy ? "Ajout…" : "Surveiller cet appareil"}
      </Button>

      <div className="space-y-2">
        {sondes.map((s) => {
          const e = ETATS[s.etat] || ETATS.en_attente;
          return (
            <div key={s.id} data-testid={`client-sonde-${s.id}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {s.etat === "hors_ligne" ? <WifiOff size={13} className="text-red-500" /> : <Wifi size={13} className="text-emerald-500" />}
                    {s.designation}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.site_nom} · {s.marque} · toutes les {s.intervalle_min} min ·{" "}
                    {s.dernier_signe ? `dernier ${fmtDate(s.dernier_signe)}` : "aucun signe reçu"}
                    {s.ticket_reference ? ` · ticket ${s.ticket_reference}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full border ${e.classe}`}>{e.label}</span>
                  <button
                    data-testid={`client-sonde-delete-${s.id}`}
                    onClick={() => supprimer(s)}
                    className="text-slate-300 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <button
                data-testid={`client-sonde-connecteur-${s.id}`}
                onClick={() => setConnecteur(connecteur === s.id ? null : s.id)}
                className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:text-[#005CFF]"
              >
                <Terminal size={13} /> {connecteur === s.id ? "Masquer le connecteur" : "Connecteur / script à coller"}
              </button>
              {connecteur === s.id && <ConnecteurSonde url={url(s)} intervalle={s.intervalle_min} marque={s.marque} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SondesClient;
